import React, { useEffect, useMemo, useRef, useState } from "react"
import { Receipt } from "../../../../../receipt/pages/ReceiptDialog"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { MRT_ColumnDef, MRT_RowSelectionState, MRT_TableInstance } from "material-react-table"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import {
    TableNumberOfItemsBox,
    TableSummaryWrap,
    TableTotalAmountBox,
} from "../../../../../../shared/components/table/TableSummaryBox"
import { Grid, MenuItem, Typography } from "@mui/material"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { MeetingServiceOrder } from "../../../../../merchant/accounting/accounting/accountingTypes"
import { DateTime } from "luxon"
import {
    ArchiveMeetingService,
    fetchUserMeetingService,
    FetchUserMeetingServiceResponse,
} from "../userMeetingServiceApi"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleEuro, faFileArrowDown, faUser } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"
import { ActionBarItem, FloatingActionBar } from "../../../../../../shared/components/table/FloatingActionBar"
import { useExportOrdersToCsv } from "../../../../../merchant/accounting/accounting/components/ExportFunctionMeetingServiceOrder"
import {
    DateType,
    SelectDateTypeFilter,
} from "../../../../../../shared/components/filters/selectDateTypeFilter/SelectDateTypeFilter"
import { StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"
import { useOrderDialogUrlState } from "../../../../../../shared/components/createOrder/hooks/useOrderDialogUrlState"
import { CreateOrderType } from "../../../../../../shared/components/createOrder/helpers"
import { UserMeetingsReceipt } from "../../../MeetingsReceipt/UserMeetingsReceipt"

const selectOrders = (data: FetchUserMeetingServiceResponse) => data?.orders || []

const useStyles = makeStyles()(() => ({
    icon: {
        paddingRight: "0.2rem",
    },
}))

export function UserMeetingService() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { openDialog } = useOrderDialogUrlState()

    const [receipt, setReceipt] = useState<Receipt>()
    const [dateType, setDateType] = useState<DateType>(DateType.ORDER_DATE)
    const { startDate, endDate } = useInitialDateFilters()
    const [selectedRows, setSelectedRows] = useState<MeetingServiceOrder[]>()
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const tableInstanceRef = useRef<MRT_TableInstance<MeetingServiceOrder>>(null)

    const [dateState, setDateState] = useState<{
        startDate: DateTime
        endDate: DateTime
    }>({
        startDate: startDate,
        endDate: endDate,
    })

    const userMeetingServiceResult = useApiQuery(fetchUserMeetingService, {
        queryName: ArchiveMeetingService.ARCHIVE_MEETING_SERVICE,
        dependencies: {
            startDate: dateState.startDate.toISODate(),
            endDate: dateState.endDate.toISODate(),
            dateType,
        },
        select: selectOrders,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(userMeetingServiceResult)

    const columns = useMemo<MRT_ColumnDef<MeetingServiceOrder>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("user-archive-meeting-service-table-id"),
                accessorKey: "id",
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("user-archive-meeting-service-table-booked-date"),
                    accessorKey: "accountingDetails.bookedTime",
                },
                (row) => row.accountingDetails.bookedTime,
            ),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("user-archive-meeting-service-table-delivery-time"),
                    accessorKey: "deliveryDetails.time",
                },
                (row) => row.deliveryDetails.time,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("user-archive-meeting-service-table-meeting"),
                accessorKey: "customer.displayName",
                Cell: ({ row }) => (
                    <Grid container gap=".75rem">
                        <LocalizedStrict
                            id="meeting-service-table-meeting"
                            vars={{ locationName: row.original.meetingDetails.meetingName }}
                        >
                            <>{row.original.meetingDetails.meetingName}</>
                        </LocalizedStrict>

                        <Typography variant="caption">
                            <FontAwesomeIcon icon={faUser} className={classes.icon} />
                            {row.original.meetingDetails.attendees?.length ?? 0}
                        </Typography>
                        {row.original.paymentDetails.isContantPayment && (
                            <Typography variant="caption">
                                <FontAwesomeIcon icon={faCircleEuro} />
                            </Typography>
                        )}
                    </Grid>
                ),
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("user-archive-meeting-service-table-amount"),
                    id: "amount",
                },
                (row) => row.amount,
                true,
            ),
        ],
        [l10n, columnFactory],
    )

    useEffect(() => {
        if (Object.keys(rowSelection).length > 0) {
            setSelectedRows(tableInstanceRef.current?.getSelectedRowModel().rows.map((row) => row.original))
        } else {
            setSelectedRows(undefined)
        }
    }, [rowSelection])

    function showReceipt(row: MeetingServiceOrder) {
        setReceipt({
            orderId: row.id,
            orderUid: row.uid,
            orderType: row.orderType,
        })
    }

    const handleClearSelected = () => {
        setSelectedRows(undefined)
        setRowSelection({})
    }

    const handleDuplicateOrder = (order: MeetingServiceOrder) => {
        openDialog({
            orderId: order.id,
            mode: "duplicate",
            activeOrderTab: CreateOrderType.MEETING_SERVICE,
        })
    }

    const exportOrders = useExportOrdersToCsv({
        data: userMeetingServiceResult.data,
        startDate: dateState.startDate || DateTime.now(),
        endDate: dateState.endDate || DateTime.now(),
    })

    return (
        <>
            <ApiQueryTable
                columns={columns}
                tableInstanceRef={tableInstanceRef}
                onRowSelectionChange={setRowSelection}
                enableRowSelection
                state={{
                    rowSelection,
                }}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <LocalizedStrict id="user-archive-meeting-service-table-action-receipt">
                        <MenuItem
                            onClick={() => {
                                showReceipt(row.original)
                                closeMenu()
                            }}
                            key={`show-receipt-${row.original.id}`}
                        >
                            Receipt
                        </MenuItem>
                    </LocalizedStrict>,
                    <LocalizedStrict id="user-archive-meeting-service-table-action-duplicate">
                        <MenuItem
                            onClick={() => {
                                handleDuplicateOrder(row.original)
                                closeMenu()
                            }}
                            key={`duplicate-${row.original.id}`}
                        >
                            Duplicate
                        </MenuItem>
                    </LocalizedStrict>,
                    <LocalizedStrict id="user-archive-meeting-service-table-action-send-message">
                        <MenuItem
                            onClick={() => {
                                //todo
                                closeMenu()
                            }}
                            key={`send-message-${row.original.id}`}
                        >
                            Send message
                        </MenuItem>
                    </LocalizedStrict>,
                ]}
                renderToolbarInternalActions={({ table }) => <StandardTableFilter variant="right" table={table} />}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <SelectDateTypeFilter dateType={dateType} setDateType={setDateType} />
                        <StandardDateRangeFilter value={dateState} onChange={setDateState} label="Date range" />
                    </StandardTableFilter>
                )}
                renderBottomToolbarCustomActions={({ table }) => (
                    <TableSummaryWrap>
                        <LocalizedStrict id="table-summary-box-number-of-orders" attrs={{ title: true }}>
                            <TableNumberOfItemsBox data={table.getFilteredRowModel().rows} title="Number of orders" />
                        </LocalizedStrict>

                        <TableTotalAmountBox
                            data={table.getFilteredRowModel().rows}
                            accessor={(row) => row.original.amount}
                        />
                    </TableSummaryWrap>
                )}
            />

            {selectedRows && (
                <FloatingActionBar selectedItemsLength={selectedRows.length} handleCloseActionBar={handleClearSelected}>
                    <LocalizedStrict id="meeting-service-table-floating-action-bar-export" attrs={{ title: true }}>
                        <ActionBarItem
                            icon={faFileArrowDown}
                            title="Export"
                            onClick={() => {
                                exportOrders()
                            }}
                        />
                    </LocalizedStrict>
                </FloatingActionBar>
            )}

            {receipt && (
                <UserMeetingsReceipt
                    open={receipt !== undefined}
                    id={receipt.orderId}
                    meetings={userMeetingServiceResult.data}
                    onClose={() => setReceipt(undefined)}
                />
            )}
        </>
    )
}
